import React from 'react';
import { ModalContainer } from '@welbe/web-design-system';

import { WithoutDoctor } from './components/WithoutDoctor/WithoutDoctor';
import { NotificationPsychology } from './components/NotificationPsychology/NotificationPsychology';
import { OcupationalDoctor } from './components/OcupationalDoctor/OcupationalDoctor';
import { Psychology } from './components/Psychology/Psychology';
import { ReactComponent as HeartBalloon } from 'App/assets/img/heart-balloon.svg';
import { ReactComponent as WarningIcon } from 'App/assets/img/warning.svg';
import { SuccessContent } from './components/SuccessContent/SuccessContent';
import {
	useCurrentModalState,
	useFirstAccess,
	useMe,
	useSuccessModal,
} from './ReferModalFlow.hooks';
import { CancelConfirmation } from './components/CancelConfirmation/CancelConfirmation';

interface ReferModalFlowProps {
	isReferVisible: boolean;
	selectedEmployees: any;
	onCloseReferFlow: () => void;
	amount: number;
	setSelectedEmployees: (e: any) => void;
	selectedCommunication: any;
	onRetryTable: any;
}

export const ReferModalFlow = ({
	isReferVisible,
	selectedEmployees,
	onCloseReferFlow,
	setSelectedEmployees,
	amount,
	selectedCommunication,
	onRetryTable,
}: ReferModalFlowProps): JSX.Element => {
	const onClearSelectedEmployees = () => {
		setSelectedEmployees([]);
	};

	const { company } = useMe();

	const { isFirstAccess, handleCloseFirstAccess } = useFirstAccess();

	const {
		isOpenSuccessModal,
		handleOpenModalSuccess,
		referedAmount,
		onSaveResults,
	} = useSuccessModal({
		onCloseReferFlow,
		onClearSelectedEmployees,
		onRetryTable,
	});

	const hasPsychology =
		company?.company?.availableExtraServices?.includes('psychology');

	const {
		currentState,
		handleGetPreviewsSteps,
		backToPreviewsStep,
		handleConfirmCancelRefer,
		handleNoDoctors,
		handleClearNoDoctors,
	} = useCurrentModalState({
		isOpenSuccessModal,
		isFirstAccess,
		hasPsychology,
		isReferVisible,
		onCloseReferFlow,
	});

	const contentBuilder = {
		successModal: {
			Content: () => (
				<SuccessContent
					alreadyReferred={referedAmount.alreadyReferred}
					success={referedAmount.success}
					error={referedAmount.error}
				/>
			),
			Icon: null,
		},
		firstAccess: {
			Content: () => (
				<NotificationPsychology onClose={handleCloseFirstAccess} />
			),
			Icon: HeartBalloon,
		},
		psycology: {
			Content: () => (
				<Psychology
					selectedEmployees={selectedEmployees}
					amount={amount}
					onSuccess={handleOpenModalSuccess}
					onCancel={() => handleGetPreviewsSteps('psycology')}
					selectedCommunication={selectedCommunication}
					onSaveResults={onSaveResults}
				/>
			),
			Icon: HeartBalloon,
		},
		ocupationalDoctor: {
			Content: () => (
				<OcupationalDoctor
					selectedEmployees={selectedEmployees}
					amount={amount}
					onCancel={() => handleGetPreviewsSteps('ocupationalDoctor')}
					onNoDoctors={handleNoDoctors}
					selectedCommunication={selectedCommunication}
					onSuccess={handleOpenModalSuccess}
					onSaveResults={onSaveResults}
				/>
			),
			Icon: HeartBalloon,
		},
		withoutDoctor: {
			Content: () => (
				<WithoutDoctor
					onClose={() => {
						onCloseReferFlow();
						handleClearNoDoctors();
					}}
				/>
			),
			Icon: WarningIcon,
		},
		cancelModal: {
			Content: () => (
				<CancelConfirmation
					onBack={backToPreviewsStep}
					onConfirm={handleConfirmCancelRefer}
				/>
			),
			Icon: null,
		},
	};

	const NoCurrentState = () => {
		onCloseReferFlow();
		return <></>;
	};

	const { Content, Icon }: any =
		currentState === '' ? NoCurrentState : contentBuilder[currentState];

	return (
		<ModalContainer
			closeOnlyOnTheButton
			visible={isReferVisible}
			headerIcon={Icon && <Icon />}
			height="fit-content"
		>
			<Content />
		</ModalContainer>
	);
};
